import type { TooltipContentProps } from '@radix-ui/react-tooltip';
import { forwardRef, memo, type ReactNode } from 'react';
import * as Tooltip from './Tooltip';
import { classNames } from '~/utils/classNames';

export type IconButtonSize = 'lg' | 'md' | 'sm' | 'xs' | '2xs';
export type IconButtonVariant = 'base' | 'ghost' | 'link';
export type IconButtonColor = 'neutral' | 'brand' | 'success' | 'warning' | 'danger';

interface BaseIconButtonProps {
  variant?: IconButtonVariant;
  color?: IconButtonColor;
  size?: IconButtonSize;
  className?: string;
  title?: string;
  side?: TooltipContentProps['side'];
  sideOffset?: TooltipContentProps['sideOffset'];
  disabled?: boolean;
  loading?: boolean;
  loader?: ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

type IconButtonWithoutChildrenProps = {
  icon: string;
  children?: undefined;
} & BaseIconButtonProps;

type IconButtonWithChildrenProps = {
  icon?: undefined;
  children: ReactNode;
} & BaseIconButtonProps;

export type IconButtonProps = IconButtonWithoutChildrenProps | IconButtonWithChildrenProps;

export const IconButton = memo(
  forwardRef<HTMLButtonElement, IconButtonProps>(({ title, side = 'bottom', sideOffset = 6, ...props }, ref) => {
    if (title) {
      return (
        <Tooltip.Provider disableHoverableContent>
          <Tooltip.Root delayDuration={300}>
            <Tooltip.Trigger asChild>
              <IconButtonImpl ref={ref} {...props} />
            </Tooltip.Trigger>

            <Tooltip.Portal>
              <Tooltip.Content side={side} align="center" sideOffset={sideOffset}>
                {title}
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      );
    }

    return <IconButtonImpl ref={ref} {...props} />;
  }),
);

const IconButtonImpl = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      icon,
      variant = 'base',
      color = 'neutral',
      size = 'lg',
      className,
      disabled = false,
      loading = false,
      loader,
      onClick,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        className={classNames(
          'flex items-center justify-center shrink-0',
          'text-bolt-ds-iconPrimary text-base leading-none',
          'disabled:cursor-not-allowed',
          'focus-visible:ring-2 outline-none',
          'relative',
          {
            'op-40': disabled,
            'size-10 rounded-lg': size === 'lg',
            'size-9 rounded-md': size === 'md',
            'size-7 rounded-md': size === 'sm',
            'size-6 rounded': size === 'xs',
            'size-5 rounded': size === '2xs',
            'text-bolt-ds-iconPrimary focus-visible:ring-bolt-ds-brandHighlight': color === 'neutral',
            'text-bolt-ds-brand focus-visible:ring-bolt-ds-brandHighlight': color === 'brand',
            'text-bolt-ds-success focus-visible:ring-bolt-ds-successHighlight': color === 'success',
            'text-bolt-ds-warning focus-visible:ring-bolt-ds-warningHighlight': color === 'warning',
            'text-bolt-ds-danger focus-visible:ring-bolt-ds-dangerHighlight': color === 'danger',
            'bg-transparent': variant === 'link',
            'bg-bolt-ds-inverseSurface/10 [&:hover:where(:not(:disabled))]:bg-bolt-ds-inverseSurface/15':
              variant === 'base' && color === 'neutral',
            'bg-transparent [&:hover:where(:not(:disabled))]:bg-bolt-ds-inverseSurface/10':
              variant === 'ghost' && color === 'neutral',
            'bg-bolt-ds-brand/12 [&:hover:where(:not(:disabled))]:bg-bolt-ds-brand/18':
              variant === 'base' && color === 'brand',
            'bg-transparent [&:hover:where(:not(:disabled))]:bg-bolt-ds-brand/10':
              variant === 'ghost' && color === 'brand',
            'bg-bolt-ds-success/12 [&:hover:where(:not(:disabled))]:bg-bolt-ds-success/18':
              variant === 'base' && color === 'success',
            'bg-transparent [&:hover:where(:not(:disabled))]:bg-bolt-ds-success/10':
              variant === 'ghost' && color === 'success',
            'bg-bolt-ds-warning/12 [&:hover:where(:not(:disabled))]:bg-bolt-ds-warning/18':
              variant === 'base' && color === 'warning',
            'bg-transparent [&:hover:where(:not(:disabled))]:bg-bolt-ds-warning/10':
              variant === 'ghost' && color === 'warning',
            'bg-bolt-ds-danger/12 [&:hover:where(:not(:disabled))]:bg-bolt-ds-danger/18':
              variant === 'base' && color === 'danger',
            'bg-transparent [&:hover:where(:not(:disabled))]:bg-bolt-ds-danger/10':
              variant === 'ghost' && color === 'danger',
          },
          className,
        )}
        disabled={disabled || loading}
        onClick={(event) => {
          if (disabled || loading) {
            return;
          }

          onClick?.(event);
        }}
        {...props}
      >
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center">
            {loader || (
              <span
                className={classNames('i-svg-spinners:90-ring-with-bg', {
                  'scale-150': size === 'lg',
                  'scale-125': size === 'md',
                  'scale-115': size === 'sm',
                  'scale-100': size === 'xs',
                })}
              />
            )}
          </div>
        )}

        <div className={loading ? 'op-0' : ''}>
          {children ? children : <div className={classNames(icon, 'size-4')}></div>}
        </div>
      </button>
    );
  },
);
